/**
 * A function that returns the field names in a formik form
 * @param {T} fields - The formik fields to retrieve.
 * @param {Array.<string>} excludedFields - The fields to exclude from the count.
 * @param {string} parentKey - An initial parent key for nested fields
 * @returns {Array.<string>} - The found fields.
 */

export const retrieveFieldKeys = <T extends {}>(
  fields: T,
  excludedFields: string[] = [],
  parentKey?: string,
  formInitialization?: boolean,
) => {
  const foundFields: string[] = [];

  Object.entries(fields).forEach(([key, value]) => {
    const currentKey = parentKey ? `${parentKey}.${key}` : key;

    if (!excludedFields.includes(currentKey)) {
      if (Array.isArray(value) || (typeof value === 'object' && value !== null)) {
        foundFields.push(
          ...retrieveFieldKeys(value as T, excludedFields, currentKey, formInitialization),
        );
      } else if (value === '' || value === undefined || value === false || formInitialization) {
        foundFields.push(extractPropertyName(parentKey ? parentKey : currentKey));
      }
    }
  });

  return foundFields;
};

/**
 * A function that counts the number of fields in a formik form
 * @param {T} fields - The formik fields to count.
 * @param {Array.<string>} excludedFields - The fields to exclude from the count.
 * @returns {number} The number of fields
 */
export const countNumFields = <T extends {}>(fields: T, excludedFields?: string[]) => {
  const result = retrieveFieldKeys(fields, excludedFields, '', true);

  if (result.length > 0) {
    return result.length;
  }
  return 0;
};

/**
 * A function that only takes the last text/string after a period (e.g "address.street")
 * @param {string} text - The text to format
 * @returns {string} The formatted text
 */
function extractPropertyName(text: string): string {
  const parts = text.split('.');
  return parts.length > 1 ? parts[parts.length - 1] : text;
}

/**
 * A function that calculates the completion percentage.
 * @param {number} fieldCount - The number of fields in the form
 * @param {number} incompleteCount - The number of incomplete fields in the form
 * @returns {number} The percentage of completed fields
 */
export function calculateCompletion(fieldCount: number, incompleteCount: number): string {
  if (incompleteCount > fieldCount) return '0';
  if (incompleteCount < 0 || fieldCount <= 0) return '0';
  const completedFields = fieldCount - incompleteCount;

  const completion = (completedFields / fieldCount) * 100;

  return Math.round(completion).toString();
}
